import { useState } from 'react';
import { Sparkles, Check, ChevronDown, ChevronUp, Copy, CheckCircle2 } from 'lucide-react';
import type { StageSuggestions } from './StageAgentHook';

export type { StageSuggestions };

interface FieldSuggestion {
  agentKey: string;
  label: string;
}

interface SuggestedFieldProps {
  label: string;
  fieldKey: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  rows?: number;
  type?: 'textarea' | 'input';
  required?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  suggestions: FieldSuggestion[];
  guidance: StageSuggestions | null;
  onRunAgent?: () => void;
  agentLoading?: boolean;
  meta?: { by?: string; at?: string; prev?: any } | null;
}

function FieldMetaLabel({ meta }: { meta?: { by?: string; at?: string; prev?: any } | null }) {
  if (!meta || !meta.by) return null;
  const when = meta.at ? new Date(meta.at).toLocaleString() : '';
  const prevStr = meta.prev == null ? '' : (typeof meta.prev === 'string' ? meta.prev : JSON.stringify(meta.prev));
  return (
    <p className="text-[10px] text-muted-foreground mt-0.5">
      Updated by <span className="font-medium">{meta.by}</span>{when ? ` · ${when}` : ''}
      {prevStr ? <> · was: <span className="italic">{prevStr.slice(0, 80)}{prevStr.length > 80 ? '…' : ''}</span></> : null}
    </p>
  );
}

function valueToString(value: any): string {
  if (!value) return '';
  if (Array.isArray(value)) return value.join('\n');
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0" title="Copy">
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function AcceptBtn({ onClick, accepted }: { onClick: () => void; accepted: boolean }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border transition-all flex-shrink-0 ${
        accepted
          ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700'
          : 'bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-400 border-violet-200 dark:border-violet-700 hover:bg-violet-100 dark:hover:bg-violet-950/50'
      }`}>
      {accepted ? <><CheckCircle2 className="h-3 w-3" />Applied</> : <><CheckCircle2 className="h-3 w-3" />Accept</>}
    </button>
  );
}

export function SuggestedField({
  label, fieldKey, value, onChange, placeholder, rows = 3,
  type = 'textarea', required, disabled, children,
  suggestions, guidance, onRunAgent, agentLoading, meta,
}: SuggestedFieldProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());

  const available = suggestions.filter(s => {
    const v = guidance?.[s.agentKey];
    return v && (typeof v === 'string' ? v.trim() : Array.isArray(v) && v.length > 0);
  });

  const hasValue = !!value?.trim();

  const handleAccept = (agentKey: string) => {
    const raw = guidance?.[agentKey];
    if (!raw) return;
    const str = valueToString(raw);
    onChange(hasValue ? `${value}\n\n${str}` : str);
    setAccepted(p => new Set([...p, agentKey]));
    setExpanded(null);
  };

  const inputClass = `w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors ${
    hasValue ? 'border-violet-300 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-950/10' : 'border-border bg-background'
  }`;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
          {hasValue && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-violet-500 font-medium"><Sparkles className="h-2.5 w-2.5" />AI</span>}
        </label>
        {!available.length && onRunAgent && (
          <button onClick={onRunAgent} disabled={agentLoading}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
            {agentLoading
              ? <><span className="h-2.5 w-2.5 border border-current border-t-transparent rounded-full animate-spin inline-block" />Generating…</>
              : <><Sparkles className="h-2.5 w-2.5" />Get AI suggestion</>}
          </button>
        )}
      </div>

      {children ? children : type === 'textarea' ? (
        <textarea value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} rows={rows} disabled={disabled} className={inputClass} />
      ) : (
        <input type="text" value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} disabled={disabled} className={inputClass} />
      )}

      <FieldMetaLabel meta={meta} />

      {available.map(s => {
        const raw = guidance![s.agentKey]!;
        const str = valueToString(raw);
        const isOpen = expanded === s.agentKey;
        const isDone = accepted.has(s.agentKey);
        return (
          <div key={s.agentKey}
            className={`rounded-lg border overflow-hidden transition-all ${
              isDone ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/10'
                     : 'border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-950/10'
            }`}>
            <div className="flex items-center gap-2 px-3 py-1.5">
              <Sparkles className={`h-3 w-3 shrink-0 ${isDone ? 'text-emerald-500' : 'text-violet-500'}`} />
              <span className={`text-[11px] font-semibold flex-1 ${isDone ? 'text-emerald-700 dark:text-emerald-400' : 'text-violet-700 dark:text-violet-400'}`}>
                {s.label}
              </span>
              <CopyBtn text={str} />
              {!isDone && <AcceptBtn accepted={false} onClick={() => handleAccept(s.agentKey)} />}
              {isDone  && <AcceptBtn accepted={true}  onClick={() => {}} />}
              <button onClick={() => setExpanded(isOpen ? null : s.agentKey)}
                className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors">
                {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            </div>
            {!isOpen && (
              <div className="px-3 pb-1.5">
                <p className="text-[11px] text-muted-foreground truncate leading-relaxed">
                  {Array.isArray(raw) ? `${raw[0]}${raw.length > 1 ? ` (+${raw.length - 1} more)` : ''}` : str.slice(0, 120)}
                </p>
              </div>
            )}
            {isOpen && (
              <div className="px-3 pb-2 border-t border-violet-100 dark:border-violet-900 mt-0.5 pt-2">
                {Array.isArray(raw) ? (
                  <ol className="space-y-1">
                    {raw.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-foreground">
                        <span className="text-violet-500 font-bold shrink-0">{i + 1}.</span><span>{item}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-[11px] text-foreground leading-relaxed whitespace-pre-wrap">{str}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
